import { hiveIdPrefix, loadIdPrefix } from "./schemas";
import { defaultIdLength } from "./utils";

// The briefing generator emits entity ids (loads and hives) as referring
// expressions instead of display names; a per-channel render pass swaps each
// id token for a component / name / link. That substitution is only safe
// because ids have a strict, prose-incompatible shape
// (`{prefix}[a-z0-9]{16,24}`), so a scanner finds them unambiguously with no
// risk of matching real words. This module is the single source of truth for
// what an "entity reference" looks like, shared by the generator's tests and
// every renderer.

/** Prefixes that count as a referenceable entity in briefing text. */
export const ENTITY_REF_PREFIXES = [loadIdPrefix, hiveIdPrefix] as const;

// Ids are generated lowercase alphanumeric, `defaultIdLength` (16) up to 24
// chars after the prefix (see getIdSchema). Match case-sensitively — matching
// uppercase would risk hitting shouted prose — and fence the token with
// lookarounds so we never match a prefix of a longer alphanumeric run or a
// ref glued onto a surrounding word.
const idBody = `[a-z0-9]{${defaultIdLength},24}`;
const prefixAlternation = ENTITY_REF_PREFIXES.map((p) =>
  p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
).join("|");

/** The RegExp source (no flags) for a single entity reference token. */
export const entityRefRegexSource = `(?<![a-z0-9_])(?:${prefixAlternation})${idBody}(?![a-z0-9])`;

/**
 * A fresh global RegExp each call — a shared global regex carries mutable
 * `lastIndex`, so handing one out would make callers stomp on each other.
 */
export function entityRefRegex(): RegExp {
  return new RegExp(entityRefRegexSource, "g");
}

export interface EntityRef {
  /** The matched id, e.g. `loa_1a2b3c4d5e6f7g8h`. */
  id: string;
  /** Index of the first character of the id in the source string. */
  start: number;
  /** Index just past the last character of the id. */
  end: number;
}

/**
 * Whether the entity reference starting at `start` in `text` is
 * sentence-initial — the first token of the string (a paragraph start), or
 * immediately preceded by a sentence terminator and whitespace (sentences
 * within a paragraph are joined by a single space).
 *
 * This is the single source of truth for the casing decision every stage-4
 * resolver makes: the generator emits entity ids as opaque atoms and never
 * re-cases them (an id must stay verbatim), so a sentence that begins with a
 * ref would render with the resolved display name's own casing. When the ref
 * is sentence-initial, resolvers uppercase the first character of the resolved
 * name; mid-sentence names are left exactly as the beekeeper named them.
 */
export function isSentenceInitialRef(text: string, start: number): boolean {
  if (start === 0) {
    return true;
  }
  return /[.!?]\s+$/.test(text.slice(0, start));
}

/** Find every entity reference in `text`, in order of appearance. */
export function matchEntityRefs(text: string): EntityRef[] {
  const refs: EntityRef[] = [];
  for (const match of text.matchAll(entityRefRegex())) {
    refs.push({
      id: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return refs;
}

/**
 * Replace every entity reference with the string `replacer` returns for its
 * id. The plain-text / email stage-4 resolver (the app uses `matchEntityRefs`
 * to splice in components instead). A sentence-initial ref
 * (`isSentenceInitialRef`) has the first character of its resolved name
 * uppercased, so a lowercase display name doesn't open a sentence in
 * lowercase; mid-sentence names are left untouched.
 */
export function replaceEntityRefs(
  text: string,
  replacer: (id: string) => string,
): string {
  return text.replace(entityRefRegex(), (id, offset: number) => {
    const name = replacer(id);
    return isSentenceInitialRef(text, offset) ? capitalizeFirst(name) : name;
  });
}

/**
 * Uppercase the first character of a resolved display name. A no-op when the
 * name already starts with an uppercase letter or a non-letter (e.g. a digit),
 * so numeric hive names like "33 East" are left alone.
 */
export function capitalizeFirst(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}
